import { useState } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { colorOptionLabel } from "@/lib/naming";

const Chip = ({ code, colors, onRemove, testid }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-terracotta bg-sand px-2 py-0.5 text-xs" data-testid={testid}>
    {colorOptionLabel(colors.find((c) => c.color_code === code) || { color_code: code })}
    {onRemove && <button type="button" onClick={onRemove} aria-label="Remove color" data-testid={`${testid}-remove`}><X className="h-3 w-3" /></button>}
  </span>
);

// ID-070: colour combinations picked from the approved colour catalog (no typing); each saved combination becomes one variation SKU
export const ComboPicker = ({ colors, combos, setCombos, min, max, draft, setDraft }) => {
  const [q, setQ] = useState("");
  const lo = Number(min) || 1;
  const hi = Number(max) || 8;
  const key = (c) => c.join("+");
  const dup = combos.some((c) => key(c) === key(draft));
  const canAdd = draft.length >= lo && draft.length <= hi && !dup;
  const s = q.trim().toLowerCase();
  const options = colors.filter((c) => !draft.includes(c.color_code) && (!s || colorOptionLabel(c).toLowerCase().includes(s)));
  const bad = combos.filter((c) => c.length < lo || c.length > hi);
  return (
    <div className="space-y-3" data-testid="design-combo-picker">
      {combos.length > 0 && (
        <ul className="space-y-1.5" data-testid="design-combo-list">
          {combos.map((c, i) => (
            <li key={key(c)} className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-white px-2 py-1.5" data-testid={`design-combo-${i}`}>
              <span className="mr-1 font-mono text-[11px] text-warmgrey">#{i + 1}</span>
              {c.map((code) => <Chip key={code} code={code} colors={colors} testid={`design-combo-${i}-${code}`} />)}
              <button type="button" onClick={() => setCombos((x) => x.filter((_, j) => j !== i))} className="ml-auto text-warmgrey hover:text-charcoal" aria-label="Remove combination" data-testid={`design-combo-${i}-remove`}><X className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
      {bad.length > 0 && <p className="text-xs text-terracotta" data-testid="design-combo-range-error">{bad.length} combination(s) no longer fit {lo}–{hi} colors — remove or rebuild them</p>}
      <div className="space-y-2 rounded-md border border-dashed border-border p-3">
        <div className="flex min-h-[28px] flex-wrap items-center gap-1.5" data-testid="design-combo-draft">
          {draft.length ? draft.map((code) => <Chip key={code} code={code} colors={colors} testid={`design-draft-${code}`} onRemove={() => setDraft((d) => d.filter((x) => x !== code))} />)
            : <span className="text-xs text-warmgrey">Pick {lo === hi ? `exactly ${lo}` : `${lo}–${hi}`} colors for a combination</span>}
        </div>
        {draft.length < hi && (
          <>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, Moroccan or US name" className="h-9 bg-white text-sm" data-testid="design-color-search" />
            <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto" data-testid="design-color-options">
              {options.map((c) => (
                <button key={c.color_code} type="button" onClick={() => { setDraft((d) => [...d, c.color_code]); setQ(""); }} data-testid={`design-color-option-${c.color_code}`}
                  className="rounded-full border border-border bg-white px-2.5 py-1 text-xs text-charcoal transition-colors hover:border-terracotta hover:bg-sand">{colorOptionLabel(c)}</button>
              ))}
              {!options.length && <span className="text-xs text-warmgrey">No matching colors</span>}
            </div>
          </>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" className="h-8 px-3 text-xs" disabled={!canAdd} onClick={() => { setCombos((x) => [...x, draft]); setDraft([]); }} data-testid="design-combo-add">
            <Plus className="mr-1 h-3 w-3" />Add combination
          </Button>
          {dup && <span className="text-xs text-terracotta" data-testid="design-combo-dup-error">This combination is already in the list</span>}
        </div>
      </div>
    </div>
  );
};
